import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format, parseISO } from "date-fns";
import { jsPDF } from 'jspdf';
import ExcelJS from "exceljs";
import { useTheme, useMediaQuery, Typography, Box, Button,
    Dialog, DialogTitle, DialogContent, DialogActions,     
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import { setTimeMode } from "state";
import FlexBetween from './FlexBetween'; 
import VietnamFlag from "img/VietnamFlag.png";
import UnitedNationsFlag from "img/UnitedNationsFlag.png";


const CrewListDialog = ({ 
    open, 
    onClose, 
    flight, 
    crewList, 
    jumpList 
}) => {
    const dispatch = useDispatch();
    const timeMode = useSelector((state) => state.auth.timemode);
    const theme = useTheme();
    const primaryDark = theme.palette.primary.dark;
    const neutralDark = theme.palette.neutral.dark;
    
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    let ordinal = 1;
    
    const getTime = (flight, timeMode, field) => 
        timeMode === 'UTC' ? flight[field] : flight[`${field}LocalBase`];
    
    const generatePDF = () => {
        const doc = new jsPDF();
        let yOffset = 20;
        let xOffset = 20;
        let pageCount = 0;

        const addPageNumber = (pageNum) => {
            const pageText = `Page ${pageNum}`;
            const textWidth = doc.getTextWidth(pageText);
            const xCenter = (doc.internal.pageSize.width - textWidth) / 2;
            const yBottom = doc.internal.pageSize.height - 10; // 10 units from bottom
            doc.text(pageText, xCenter, yBottom);
        };

        const drawLine = (yPosition) => {
            doc.line(xOffset, yPosition, doc.internal.pageSize.width - xOffset, yPosition); // Draw line
        };

        const flightNo = flight.flightNo;
        const flightDate = format(parseISO(flight.flightDate), "ddMMMyyyy");

        // Add Title
        doc.setFontSize(20);
        doc.setFont("helvetica", "bold");
        doc.text('CREW LIST', 105, yOffset, null, null, 'center');
        yOffset += 15;

        // Add Flight Info        
        doc.setFontSize(18);    
        doc.setFont("helvetica", "normal");    
        if (flight) {
            doc.text(                
                timeMode === 'UTC' 
                    ? `${format(flight.flightDate, "EEE dd/MM/yyyy")} | ${flight.flightDesc} ${flight.dep}-${flight.arr}`
                    : `${format(flight.flightDateLocalBase, "EEE dd/MM/yyyy")} | ${flight.flightDesc} ${flight.dep}-${flight.arr}`,
                20, yOffset
            );
            yOffset += 10;
            
            doc.text(
                `AC Type: ${flight.acType} | Tail: ${flight.flightReg}`,
                20, yOffset
            );
            yOffset += 10;

            doc.text(                
                `Etd: ${getTime(flight, timeMode, 'etd')} (${flight.etd}Z) | Eta: ${getTime(flight, timeMode, 'eta')} (${flight.eta}Z)`,                
                20, yOffset
            );
            yOffset += 10;
        }

        // Draw a line 
        drawLine(yOffset);
        yOffset += 10;

        // Crew List Section
        doc.setFontSize(16);
        if (crewList && crewList.length > 0) {            
            crewList.forEach((crew) => {
                doc.text(
                    `${crew.crewName || 'N/A'} | ${crew.crewCode || ''} | ${crew.post || ''} | ${crew.base || ''}${crew.pax ? ` | (${crew.pax})` : ''}${crew.trnDuty ? ` | (${crew.trnDuty})` : ''}`,
                    20, yOffset
                );
                yOffset += 10;

                // If yOffset exceeds page height, add new page
                if (yOffset > 280) {
                    doc.addPage();
                    yOffset = 20;
                }

                if (yOffset > doc.internal.pageSize.height - 30) {
                    addPageNumber(++pageCount);
                    doc.addPage();
                    yOffset = 20; // Reset yOffset for new page
                }
            });
        }

        // Jump List Section
        if (jumpList && jumpList.length > 0) {
            jumpList.filter((staff) => staff.seaterType !== 'crew').forEach((staff) => {
                doc.text(
                    `${staff.staffName || 'N/A'} | (JS)`,
                    20, yOffset
                );
                yOffset += 10;

                // If yOffset exceeds page height, add new page
                if (yOffset > 280) {
                    doc.addPage();
                    yOffset = 20;
                }

                if (yOffset > doc.internal.pageSize.height - 30) {
                    addPageNumber(++pageCount);
                    doc.addPage();
                    yOffset = 20; // Reset yOffset for new page
                }
            });
        }

        if (yOffset > doc.internal.pageSize.height - 30) {
            addPageNumber(++pageCount);
            doc.addPage();
            yOffset = 20; // Reset yOffset for new page
        }

        // Save the PDF
        doc.save(`CrewList_${flightNo}_${flightDate}.pdf`);
    };

    const generateExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("FlightInfoNote");
    
        worksheet.mergeCells("A1:J1");
        const cellA1 = worksheet.getCell("A1");
        cellA1.value = "FLIGHT INFORMATION";
        cellA1.alignment = { vertical: "middle", horizontal: "center" };
        cellA1.font = { bold: true, size: 14 };

        // Merge and style sub-header row
        worksheet.mergeCells("A2:J2");
        const cellA2 = worksheet.getCell("A2");
        cellA2.value = "Times in UTC";
        cellA2.alignment = { vertical: "middle", horizontal: "center" };
        cellA2.font = { italic: true };

        worksheet.addRow();

        // Define the header row
        const headerRow = worksheet.addRow([
            "DATE", 
            "FLT", 
            "REG", 
            "AC", 
            "DEP", 
            "ARR", 
            "STD", 
            "STA",
            "JumpSeaters",
            "Crew", 
        ]);
    
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, size: 11 };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD3D3D3" }, // Light gray
            };
        });
    
        // Set column widths
        worksheet.columns = [
            { width: 12 }, // DATE
            { width: 8 },  // FLT
            { width: 10 }, // REG
            { width: 8 },  // AC
            { width: 8 },  // DEP
            { width: 8 },  // ARR
            { width: 8 },  // STD
            { width: 8 },  // STA
            { width: 40 }, // JumpSeaters            
            { width: 40 }, // Crew            
        ];
        
        const startRow = 5; // Starting data row
        const maxLength = Math.max(jumpList.length, crewList.length);

        if (jumpList && jumpList.length > 0) {
            jumpList.forEach((seater, index) => {
                worksheet.getCell(`I${startRow + index}`).value = seater.staffName;
            });

            if (maxLength > jumpList.length) {
                for (let i = jumpList.length - 1; i < maxLength; i++) {
                    worksheet.getCell(`I${startRow + i}`).value = "";
                }
            }
        }

        if (crewList && crewList.length > 0) {
            crewList.forEach((crew, index) => {
                worksheet.getCell(`J${startRow + index}`).value = `${crew.crewName} (${crew.post}) ${crew.crewCode}`;
            });

            if (maxLength > crewList.length) {
                for (let i = crewList.length - 1; i < maxLength; i++) {
                    worksheet.getCell(`I${startRow + i}`).value = "";
                }
            }
        }

        const endRow = startRow + maxLength - 1; // Dynamic end row

        // Apply styles (borders and wrapping)
        const applyCellStyles = (cell) => {
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
            cell.alignment = { wrapText: true, vertical: "middle", horizontal: "center" };
        };

        // Populate flight details and merge relevant columns
        const flightDetails = [
            format(flight.flightDate, "dd/MM/yyyy"), 
            flight.flightNo, 
            flight.flightReg, 
            flight.acType, 
            flight.dep, 
            flight.arr, 
            flight.etd, 
            flight.eta
        ];
        
        flightDetails.forEach((value, index) => {
            const colLetter = String.fromCharCode(65 + index); // A, B, C, ...
            const cell = worksheet.getCell(`${colLetter}${startRow}`);
            cell.value = value;
            applyCellStyles(cell);
            worksheet.mergeCells(`${colLetter}${startRow}:${colLetter}${endRow}`);
        });
                
        worksheet.eachRow((row) => {
            row.eachCell(applyCellStyles);
        });
    
        // Add footer note
        // const footerRow = worksheet.addRow(["Note: ..."]);
        // footerRow.getCell(1).alignment = { vertical: "middle" };
        // worksheet.mergeCells(`A${footerRow.number}:J${footerRow.number}`);
    
        // Save the file
        // await workbook.xlsx.writeFile("FlightInfoNote.xlsx");
        
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = "FlightInfoNote.xlsx";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };
    
    
    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            sx={{ '& .MuiDialog-paper': { minWidth: isMobile ? '90%' : '55%' } }}
        >
            <DialogTitle borderBottom={`1px solid ${neutralDark}`}>
                <FlexBetween>
                    <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        color={primaryDark} 
                    >
                        CREW LIST
                    </Typography>
                    <Box 
                        display="flex" 
                        alignItems="center"
                        onClick={() => dispatch(setTimeMode())}
                    >                            
                        <img
                            alt="Flag"
                            width={25}
                            height={25}                           
                            src={timeMode === "UTC" ? UnitedNationsFlag : VietnamFlag}
                            style={{ 
                                cursor: "pointer", 
                                objectFit: "cover", 
                                borderRadius: "50%",
                                transition: "opacity 0.3s",
                            }}
                            onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                        />
                    </Box>
                </FlexBetween>                    
            </DialogTitle>

            <DialogContent>
                {!flight ? (
                    <Typography>No flights available</Typography>
                ) : (                    
                    <Box
                        borderBottom={`1px solid ${neutralDark}`} 
                        padding="4px 0"
                    >
                        <Box padding="4px 0" borderBottom={`1px solid ${neutralDark}`} >
                            <Typography variant="h6">
                                {timeMode === 'UTC' ? (
                                    flight?.flightDate ? (
                                        `${format(flight.flightDate, "EEE dd/MM/yyyy")} | ${flight.flightDesc} ${flight.dep}-${flight.arr}`
                                    ) : (
                                        "Invalid Date"
                                    )
                                ) : (
                                    flight?.flightDateLocalBase ? (
                                        `${format(flight.flightDateLocalBase, "EEE dd/MM/yyyy")} | ${flight.flightDesc} ${flight.dep}-${flight.arr}`
                                    ) : (
                                        "Invalid Date"
                                    )
                                )}
                            </Typography>

                            <Typography variant="h6">
                                {`AC Type: ${flight.acType} | Tail: ${flight.flightReg}`}                   
                            </Typography>

                            <FlexBetween>
                                <Typography variant="h6">
                                    Etd: {getTime(flight, timeMode, 'etd')} ({flight.etd}Z)                  
                                </Typography>

                                <Typography variant="h6">
                                    Eta: {getTime(flight, timeMode, 'eta')} ({flight.eta}Z)                  
                                </Typography>
                            </FlexBetween>
                            
                            <FlexBetween>
                                <Typography variant="h6">
                                    {flight.atd && `Atd: ${getTime(flight, timeMode, 'atd')} (${flight.atd}Z)`}                 
                                </Typography>

                                <Typography variant="h6">
                                    {flight.ata && `Atd: ${getTime(flight, timeMode, 'ata')} (${flight.ata}Z)`}                 
                                </Typography>
                            </FlexBetween> 
                        </Box>

                        {!crewList && !jumpList && (
                            <Typography>No crew list</Typography>
                        )} 

                        {crewList && crewList.map((crew, idx) => (
                            <Box
                                key={idx}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"  
                                mt="4px" 
                            >             
                                <Box 
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    gap={0.5}
                                >
                                    <Typography width="18px" textAlign='right' color={primaryDark}>
                                        {ordinal++}. 
                                    </Typography>
                                    <Typography flex={1} color={primaryDark}>
                                        {crew.crewName  || 'N/A'}
                                    </Typography>
                                </Box>
                                <Box 
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    gap={0.5}
                                >
                                    <Typography width={isMobile ? "30px" : "42px"} textAlign="right">{crew.crewCode || ''}</Typography>
                                    <Typography width={isMobile ? "20px" : "40px"} textAlign="center">{crew.post || ''}</Typography>
                                    <Typography width={isMobile ? "25px" : "32px"} textAlign="center">{crew.base || ''}</Typography>
                                    <Typography width="10px" textAlign="center">{crew.pax || ''}</Typography>
                                    <Typography width="10px" textAlign="center">{crew.trnDuty || ''}</Typography>
                                    {crew.contactNo 
                                        ? (
                                            <Typography
                                                width="16px"
                                                aria-label={`Call ${crew.contactNo}`}
                                                title={`Call ${crew.contactNo}`}
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="flex-end"
                                                component="a"
                                                href={`tel:${crew.contactNo.replace(/\D/g, '').trim()}`}
                                                sx={{
                                                    textDecoration: 'none',
                                                    cursor: 'pointer',
                                                    color: 'yellow', 
                                                }}
                                            >
                                                <PhoneIcon /> 
                                            </Typography>
                                        ) : (
                                            <Typography
                                                width="16px"
                                                aria-label="No contact number"
                                                title="No contact number"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="flex-end"
                                                sx={{
                                                    textDecoration: 'none',
                                                    cursor: 'not-allowed',
                                                    color: 'grey', 
                                                }}
                                            >
                                                <PhoneDisabledIcon />
                                            </Typography>
                                        )
                                    }
                                </Box>
                            </Box>
                        ))}

                        {jumpList && jumpList.filter((staff) => staff.seaterType !== 'crew')
                                .map((staff, idx) => (
                            <Box
                                key={idx}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"  
                                mt="4px"   
                            >                                
                                <Box 
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    gap={0.5}
                                >
                                    <Typography width="18px" align='right' color={primaryDark}>
                                        {ordinal++}.
                                    </Typography>
                                    <Typography flex={1} color={primaryDark}>
                                        {staff.staffName || 'N/A'}
                                    </Typography>
                                </Box>
                                <Box 
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    gap={0.5}
                                >
                                    {/* <Typography width="30px" textAlign="center">{""}</Typography>
                                    <Typography width="20px" textAlign="center">{""}</Typography>
                                    <Typography width="25px" textAlign="center">{""}</Typography> */}
                                    <Typography width="32px" textAlign="center">{"JS"}</Typography> 
                                    {/* <Typography width="10px" textAlign="center">{""}</Typography> */}
                                    {staff.contactNo 
                                        ? (
                                            <Typography
                                                width="20px"
                                                aria-label={`Call ${staff.contactNo}`}
                                                title={`Call ${staff.contactNo}`}
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                textAlign="center"
                                                component="a"
                                                href={`tel:${staff.contactNo.replace(/\D/g, '').trim()}`}                                            
                                                sx={{
                                                    textDecoration: 'none',
                                                    cursor: 'pointer',
                                                    color: 'yellow', 
                                                }}
                                            >
                                                <PhoneIcon />
                                            </Typography>
                                        ) : (<Typography
                                            width="16px"
                                            aria-label="No contact number"
                                            title="No contact number"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            textAlign="center"
                                            sx={{
                                                textDecoration: 'none',
                                                cursor: 'not-allowed',
                                                color: 'grey', 
                                            }}
                                        >
                                            <PhoneDisabledIcon />
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>   
                <Button variant="outlined" onClick={generateExcel}>
                    Excel
                </Button>        
                <Button variant="outlined" onClick={generatePDF}>
                    PDF
                </Button>          
                <Button variant="outlined" color="secondary" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CrewListDialog;
